// @ts-strict-ignore
import moment from 'moment';
import type { PayloadAction } from '@reduxjs/toolkit';

import { convertToCamel } from '~/helpers/api';
import type { ResponseException } from '~/helpers/exceptionHandler';

import type {
  AlertManualReview,
  Investigation,
  InvestigationAgent,
  InvestigationViewAlert,
} from '../types';

import { convertToFilters, convertToInvestigationRow } from './helpers';
import { initialInvestigationsState } from './state';
import type {
  DocumentType,
  InvestigationDocumentResponse,
  InvestigationSecondaryData,
  AssignAlertAgentResponse,
  InvestigationsQueueResponse,
  InvestigationsState,
  InvestigationReviewsByToken,
  UnassignAlertAgentResponse,
  InvestigationQueueSavedSearches,
  InvestigationNotesTimelineObject,
  InvestigationTagsTimelineObject,
  InvestigationSummaryResponse,
  InvestigationQueueFiltersResponse,
  InvestigationTagOutcomeReasonsOptions,
} from './types';

export default {
  mergeInvestigationsQueue: (
    state,
    { payload }: PayloadAction<InvestigationsQueueResponse>,
  ) => {
    const {
      Embedded,
      error,
      investigations,
    } = convertToCamel(payload);
    const pagination = Embedded?.pagination || initialInvestigationsState.queue.pagination;
    const sorting = Embedded?.sorting || initialInvestigationsState.queue.sorting;
    return ({
      ...state,
      queue: {
        ...state.queue,
        pagination,
        sorting,
        error,
        investigations: convertToInvestigationRow(
          investigations,
        ),
      },
    });
  },

  mergeInvestigationsQueuePagination: (
    state,
    {
      payload: {
        page,
        perPage,
      },
    }: PayloadAction<{
      page: number;
      perPage: number;
    }>,
  ) => ({
    ...state,
    queue: {
      ...state.queue,
      pagination: {
        ...state.queue.pagination,
        page,
        perPage,
      },
    },
  }),

  mergeInvestigationQueueTagsOutcomeReasons: (
    state,
    { payload }: PayloadAction<{
      payload: InvestigationTagOutcomeReasonsOptions;
    }>,
  ) => ({
    ...state,
    queue: {
      ...state.queue,
      filters: {
        ...state.queue.filters,
        ...payload.payload,
      },
    },
  }),
  mergeInvestigationsQueueFilters: (
    state,
    { payload }: PayloadAction<{
      payload: InvestigationQueueFiltersResponse;
    }>,
  ) => ({
    ...state,
    queue: {
      ...state.queue,
      filters: {
        ...state.queue.filters,
        ...(convertToFilters(
          payload.payload,
        )),
      },
    },
  }),

  mergeInvestigationsQueueSavedSearches: (
    state,
    { payload }: PayloadAction<InvestigationQueueSavedSearches>,
  ) => ({
    ...state,
    queue: {
      ...state.queue,
      savedSearches: payload.searches,
    },
  }),

  mergeInvestigationsQueueError: (
    state,
    { payload: error }: PayloadAction<Error>,
  ) => {
    const code = error.cause || 500;
    return {
      ...state,
      queue: {
        ...state.queue,
        error: {
          code,
          message: error.message,
        },
      },
    };
  },

  mergeInvestigation: (
    state,
    { payload }: PayloadAction<Investigation>,
  ) => ({
    ...state,
    investigation: {
      ...state.investigation,
      ...payload,
    },
  }),

  mergeInvestigationReviewsByToken: (
    state,
    { payload: investigationReviewsByToken }: PayloadAction<InvestigationReviewsByToken>,
  ) => ({
    ...state,
    investigation: {
      ...state.investigation,
      investigationReviewsByToken,
    },
  }),

  mergeInvestigationSummary: (
    state,
    { payload: investigationSummary }: PayloadAction<InvestigationSummaryResponse>,
  ) => ({
    ...state,
    investigation: {
      ...state.investigation,
      investigationSummary,
    },
  }),

  mergeInvestigationEvidence: (
    state,
    { payload },
  ) => {
    const evidenceData = convertToCamel(payload);
    return {
      ...state,
      investigation: {
        ...state.investigation,
        investigationEvidenceEvents: evidenceData.events || [],
      },
    };
  },

  mergeAssignableAgents: (state, { payload }: PayloadAction<InvestigationAgent[]>) => {
    const assignableAgents = convertToCamel(payload);
    return {
      ...state,
      assignableAgents,
    };
  },

  mergeDocumentTypes: (state, { payload }: PayloadAction<DocumentType[]>) => ({
    ...state,
    documentTypes: payload,
  }),

  mergeAssignedAgent: (state, { payload }: PayloadAction<string>) => {
    const assignedAgent = state.assignableAgents.find(
      assignableAgent => assignableAgent.token === payload,
    );
    return {
      ...state,
      investigation: {
        ...state.investigation,
        investigationAssignedAgent: assignedAgent,
      },
    };
  },

  mergeUnassignInvestigationAgent: state => (
    {
      ...state,
      investigation: {
        ...state.investigation,
        investigationAssignedAgent: null,
      },
    }
  ),

  mergeAlertAssignedAgent: (state, {
    payload: {
      assignedAgent,
      journeyApplicationToken,
    },
  }: PayloadAction<AssignAlertAgentResponse>) => ({
    ...state,
    investigation: {
      ...state.investigation,
      journeyAlertsByToken: {
        ...state.investigation.journeyAlertsByToken,
        [journeyApplicationToken]: {
          ...state.investigation.journeyAlertsByToken[journeyApplicationToken],
          assignedAgent,
        },
      },
    },
  }),

  mergeAlertUnassignedAgent: (state, {
    payload: {
      journeyApplicationToken,
    },
  }: PayloadAction<UnassignAlertAgentResponse>) => ({
    ...state,
    investigation: {
      ...state.investigation,
      journeyAlertsByToken: {
        ...state.investigation.journeyAlertsByToken,
        [journeyApplicationToken]: {
          ...state.investigation.journeyAlertsByToken[journeyApplicationToken],
          assignedAgent: null,
        },
      },
    },
  }),

  mergeInvestigationViewError: (
    state,
    { payload: error }: PayloadAction<ResponseException>,
  ) => {
    const code = error.cause || error.response.status || 500;
    return {
      ...state,
      investigation: {
        ...state.investigation,
        evidences: [],
        error: {
          code,
          message: error.message,
        },
      },
    };
  },

  mergeJourneyAlert: (
    state,
    { payload: journeyAlert }: PayloadAction<InvestigationViewAlert>,
  ) => ({
    ...state,
    investigation: {
      ...state.investigation,
      evaluationsByToken: {
        ...state.investigation.evaluationsByToken,
        ...journeyAlert.evaluationsByToken,
      },
      journeyAlertsByToken: {
        ...state.investigation.journeyAlertsByToken,
        [journeyAlert.journeyApplicationToken]: journeyAlert,
      },
    },
  }),

  mergeJourneyAlertAssignableAgents: (
    state,
    {
      payload: {
        journeyApplicationToken,
        alertAssignableAgents,
      },
    }: PayloadAction<{
      journeyApplicationToken: string;
      alertAssignableAgents: InvestigationAgent[];
    }>,
  ) => ({
    ...state,
    investigation: {
      ...state.investigation,
      journeyAlertsByToken: {
        ...state.investigation.journeyAlertsByToken,
        [journeyApplicationToken]: {
          ...state.investigation.journeyAlertsByToken[journeyApplicationToken],
          assignableAgents: alertAssignableAgents,
        },
      },
    },
  }),


  mergeSecondaryInvestigationData: (
    state,
    { payload: secondaryData }: PayloadAction<InvestigationSecondaryData>,
  ) => ({
    ...state,
    investigation: {
      ...state.investigation,
      ...secondaryData,
    },
  }),

  mergeAlertManualReview: (
    state,
    { payload: alertManualReview }: PayloadAction<AlertManualReview>,
  ) => ({
    ...state,
    investigation: {
      ...state.investigation,
      alertManualReviewsByToken: {
        ...state.investigation.alertManualReviewsByToken,
        [alertManualReview.alertManualReviewToken]: alertManualReview,
      },
    },
  }),

  mergeTimelineItems: state => {
    const {
      journeyAlertsByToken = {},
      investigationReviewsByToken = {},
      investigationNotes = [],
      investigationEvidences = { tags: [] },
      investigationDocuments = [],
      investigationEvents = [],
      investigationEvidenceEvents = [],
    } = (state as InvestigationsState).investigation;
    const { tags = [] } = investigationEvidences;
    const journeyAlerts = Object.values(journeyAlertsByToken).map(journeyAlert => ({
      type: 'journeyAlert',
      timestamp: Math.floor(moment.utc(journeyAlert.journeyApplicationCreatedAt).valueOf() / 1000),
      journeyAlertToken: journeyAlert.journeyApplicationToken,
    }));
    const investigationReviews = Object.values(investigationReviewsByToken).map(review => ({
      type: 'investigationReview',
      timestamp: Math.floor(review.reviewedAt),
      uiReviewToken: review.uiReviewToken,
    }));
    const tagEvidencesByTimestamp: { [timestamp: string]: Omit<InvestigationTagsTimelineObject, 'timestamp' | 'type'> } = tags
      .filter(tag => !tag.investigationNoteId)
      .reduce((acc, curr) => {
        const { addedBy, createdAt, ...rest } = curr;
        if (!acc[createdAt]) {
          acc[createdAt] = { createdBy: addedBy, tags: [rest] };
        } else {
          acc[createdAt].tags.push(rest);
        }
        return acc;
      }, {});
    const tagEvidences: InvestigationTagsTimelineObject[] = Object.entries(tagEvidencesByTimestamp)
      .map(([timestamp, { createdBy, tags: tagsArray }]) => ({
        type: 'tags' as const,
        createdBy,
        timestamp: parseInt(timestamp, 10),
        tags: tagsArray,
      }));
    const invNotes: InvestigationNotesTimelineObject[] = investigationNotes
      .filter(note => note.isInvestigationLevelNote)
      .map(note => ({
        type: 'note' as const,
        timestamp: Math.floor(note.createdAt),
        text: note.text,
        createdBy: note.createdBy,
        isInvestigationLevelNote: note.isInvestigationLevelNote,
        tags: note?.tags || [],
      }));

    const documentsByTimestamp = investigationDocuments.reduce((acc, document) => {
      const { created_at: createdAt } = document;
      const documentTimestamp = Math.floor(moment.utc(createdAt).valueOf() / 1000);
      if (!acc[documentTimestamp]) {
        acc[documentTimestamp] = [document];
      } else {
        acc[documentTimestamp].push(document);
      }
      return acc;
    }, {});

    // Merge document groups into nearby notes or tag evidences (within 5s)
    // so they appear as a single timeline entry when submitted together.
    const MERGE_WINDOW_SECONDS = 5;
    const consumedDocTimestamps = new Set<string>();

    for (const note of invNotes) {
      for (const [docTs, docs] of Object.entries(documentsByTimestamp)) {
        if (
          !consumedDocTimestamps.has(docTs)
          && Math.abs(note.timestamp - parseInt(docTs, 10)) <= MERGE_WINDOW_SECONDS
        ) {
          note.documents = [
            ...(note.documents || []),
            ...(docs as InvestigationDocumentResponse[]),
          ];
          consumedDocTimestamps.add(docTs);
        }
      }
    }

    for (const tagEvidence of tagEvidences) {
      for (const [docTs, docs] of Object.entries(documentsByTimestamp)) {
        if (
          !consumedDocTimestamps.has(docTs)
          && Math.abs(tagEvidence.timestamp - parseInt(docTs, 10)) <= MERGE_WINDOW_SECONDS
        ) {
          tagEvidence.documents = [
            ...(tagEvidence.documents || []),
            ...(docs as InvestigationDocumentResponse[]),
          ];
          consumedDocTimestamps.add(docTs);
        }
      }
    }

    const timelineDocuments = Object.entries(documentsByTimestamp)
      .filter(([timestamp]) => !consumedDocTimestamps.has(timestamp))
      .map(([timestamp, documentsArray]: [string, InvestigationDocumentResponse[]]) => {
        const entityTokens = [...new Set(
          documentsArray
            .map(doc => doc.entity_token)
            .filter(Boolean),
        )];

        return {
          type: 'documents',
          documents: documentsArray,
          timestamp: parseInt(timestamp, 10),
          entityTokens,
        };
      });

    // sort investigation events into their proper buckets and format
    const {
      archivedUnarchivedEvents,
      transactionWebhookAuditEvents,
      bankAccountWebhookAuditEvents,
    } = investigationEvents.reduce((acc, {
      event, agent, timestamp, meta,
    }) => {
      if (['investigation_archived', 'investigation_unarchived'].includes(event)) {
        acc.archivedUnarchivedEvents.push({
          type: event === 'investigation_archived' ? 'archived' : 'unarchived',
          createdBy: agent,
          timestamp: Math.floor(moment.utc(timestamp).valueOf() / 1000),
        });
      } else if (['transaction_status_update_webhook', 'bank_account_status_update_webhook'].includes(event)) {
        acc.bankAccountWebhookAuditEvents.push({
          agent,
          timestamp: Math.floor(moment.utc(timestamp).valueOf() / 1000),
          type: event,
          webhookAuditEventToken: meta?.webhookAuditEventToken,
        });
      }

      return acc;
    }, {
      archivedUnarchivedEvents: [],
      transactionWebhookAuditEvents: [],
      bankAccountWebhookAuditEvents: [],
    });

    // Format evidence events for timeline
    const evidenceItems = investigationEvidenceEvents.map((event, index) => ({
      type: 'evidence',
      timestamp: Math.floor(moment.utc(event.createdAt).valueOf() / 1000),
      notes: event.notes || [],
      documents: event.documents || [],
      evidenceSources: event.evidenceSources,
      createdBy: event.createdBy,
      key: `evidence-${event.createdAt}-${index}`,
    }));

    const timeline = [
      ...journeyAlerts,
      ...investigationReviews,
      ...invNotes,
      ...tagEvidences,
      ...timelineDocuments,
      ...archivedUnarchivedEvents,
      ...transactionWebhookAuditEvents,
      ...bankAccountWebhookAuditEvents,
      ...evidenceItems,
    ].sort((a, b) => b.timestamp - a.timestamp);

    return {
      ...state,
      investigation: {
        ...state.investigation,
        investigationTimelineItems: timeline,
      },
    };
  },

  mergeWebhookAuditEventDetails: (
    state,
    { payload: webhookAuditEventDetails },
  ) => ({
    ...state,
    investigation: {
      ...state.investigation,
      webhookAuditEventDetails: {
        ...(state.investigation?.webhookAuditEventDetails || {}),
        ...webhookAuditEventDetails,
      },
    },
  }),

  mergeInvestigationFetched: (
    state,
    { payload: fetched }: PayloadAction<boolean>,
  ) => ({
    ...state,
    fetched,
  }),

  mergeArchivedInvestigation: (
    state,
    { payload }: PayloadAction<{
      isArchiving: boolean;
      archivedFilter: string | [];
      investigationTokens: string[];
    }>,
  ) => {
    let investigations = [...state.queue.investigations];
    if (payload.archivedFilter === 'all') {
      investigations = state.queue.investigations.map(investigation => {
        if (payload.investigationTokens.find(token => investigation.token === token)) {
          return {
            ...investigation,
            archivedAt: payload.isArchiving ? new Date().toDateString() : null,
          };
        }
        return investigation;
      });
    } else {
      investigations = state.queue.investigations.filter(
        investigation => !payload.investigationTokens.find(
          token => investigation.token === token,
        ));
    }
    return {
      ...state,
      queue: {
        ...state.queue,
        investigations: [
          ...investigations,
        ],
      },
    };
  },

  resetInvestigationState: () => initialInvestigationsState,
};
